import { __assign, __read, __spreadArray } from "tslib";
import { useMemo, useReducer } from 'react';
import { parse } from 'date-fns';
import { getCurrency } from '../../../cache';
import { API_DATE_FORMAT, format } from '../../../utils';
var reducer = function (state, action) {
    var _a, _b;
    if (action.type === 'change') {
        return __assign(__assign({}, state), (_a = {}, _a[action.segmentId] = __assign(__assign({}, state[action.segmentId]), (_b = {}, _b[action.travellerId] = !state[action.segmentId][action.travellerId], _b)), _a));
    }
    else if (action.type === 'clear') {
        var clearState = __assign({}, state);
        for (var segmentId in clearState) {
            for (var passengerId in clearState[segmentId]) {
                clearState[segmentId][passengerId] = false;
            }
        }
        return clearState;
    }
    return state;
};
export var useAnimalState = function (services, segments, travellers) {
    var actualService = services[0];
    var allowedSegmentIdsMap = new Map(actualService.allowedSegments.map(function (ids) { return [ids[0], ids]; }));
    var initialState = useMemo(function () {
        var state = {};
        segments.forEach(function (segment) {
            if (allowedSegmentIdsMap.has(segment.id)) {
                travellers.forEach(function (traveller) {
                    var _a, _b;
                    var _c, _d, _e;
                    var selectedService = (_e = (_d = (_c = traveller.services) === null || _c === void 0 ? void 0 : _c.gdsServices) === null || _d === void 0 ? void 0 : _d.services) === null || _e === void 0 ? void 0 : _e.find(function (travellerService) {
                        return travellerService.serviceId === actualService.id &&
                            travellerService.segmentIds.includes(segment.id);
                    });
                    state = __assign(__assign({}, state), (_a = {}, _a[segment.id] = __assign(__assign({}, state[segment.id]), (_b = {}, _b[traveller.id] = !!selectedService, _b)), _a));
                });
            }
        });
        return state;
    }, [segments, travellers, actualService, allowedSegmentIdsMap]);
    var _a = __read(useReducer(reducer, initialState), 2), state = _a[0], dispatch = _a[1];
    var servicesBySegment = useMemo(function () {
        var servicesMap = new Map();
        segments.forEach(function (segment) {
            var _a, _b;
            if (allowedSegmentIdsMap.has(segment.id)) {
                var allowedSegmentIds = allowedSegmentIdsMap.get(segment.id);
                var allowedTravellerIds_1 = new Set(actualService.allowedPassengers.reduce(function (acc, ids) { return __spreadArray(__spreadArray([], __read(acc), false), __read(ids), false); }, []));
                var direction = [segment.departure.airport.city.name, segment.arrival.airport.city.name];
                if (allowedSegmentIds.length > 1) {
                    var _c = __read([
                        allowedSegmentIds[0],
                        allowedSegmentIds[allowedSegmentIds.length - 1]
                    ], 2), segmentDepartureId_1 = _c[0], segmentArrivalId_1 = _c[1];
                    var departureCity = (_a = segments.find(function (segment) { return segment.id === segmentDepartureId_1; })) === null || _a === void 0 ? void 0 : _a.departure.airport.city.name;
                    var arrivalCity = (_b = segments.find(function (segment) { return segment.id === segmentArrivalId_1; })) === null || _b === void 0 ? void 0 : _b.arrival.airport.city.name;
                    direction = [departureCity, arrivalCity];
                }
                var price = __assign(__assign({}, actualService.price), { amount: actualService.price.amount * allowedSegmentIds.length });
                var serviceBySegment = {
                    segment: segment,
                    price: price,
                    service: actualService,
                    travellers: travellers.filter(function (traveller) { return allowedTravellerIds_1.has(traveller.id); }),
                    date: format(parse(segment.departure.date, API_DATE_FORMAT, new Date()), 'dd MMM'),
                    direction: direction.filter(Boolean).join(' – ')
                };
                servicesMap.set(segment.id, serviceBySegment);
            }
        });
        return __spreadArray([], __read(servicesMap.entries()), false);
    }, [segments, travellers, actualService, allowedSegmentIdsMap]);
    var _b = __read(useMemo(function () {
        var services = [];
        var servicesToSave = [];
        for (var segmentId in state) {
            for (var passengerId in state[segmentId]) {
                var serviceId = actualService.id;
                var isSelected = state[segmentId][passengerId];
                var selectedService = {
                    serviceId: serviceId,
                    segmentId: segmentId,
                    passengerId: passengerId,
                    segmentIds: allowedSegmentIdsMap.get(segmentId),
                    service: actualService,
                    count: isSelected ? 1 : 0
                };
                if (isSelected) {
                    services = __spreadArray(__spreadArray([], __read(services), false), [selectedService], false);
                }
                if (isSelected !== initialState[segmentId][passengerId]) {
                    servicesToSave = __spreadArray(__spreadArray([], __read(servicesToSave), false), [selectedService], false);
                }
            }
        }
        return [services, servicesToSave];
    }, [initialState, state, actualService, allowedSegmentIdsMap]), 2), selectedServices = _b[0], servicesToSave = _b[1];
    var hasChanges = useMemo(function () { return JSON.stringify(initialState) !== JSON.stringify(state); }, [initialState, state]);
    var totalPrice = useMemo(function () {
        var filterServices = selectedServices.filter(function (service) { return service.count > 0; });
        if (filterServices.length) {
            var amount = filterServices.reduce(function (acc, service) {
                return acc + service.service.price.amount * allowedSegmentIdsMap.get(service.segmentId).length;
            }, 0);
            return {
                currency: getCurrency(),
                amount: amount
            };
        }
        return null;
    }, [selectedServices, allowedSegmentIdsMap]);
    var onChange = function (segmentId, travellerId) {
        dispatch({ type: 'change', segmentId: segmentId, travellerId: travellerId });
    };
    var onClear = function () {
        dispatch({ type: 'clear' });
    };
    return { state: state, servicesBySegment: servicesBySegment, selectedServices: selectedServices, servicesToSave: servicesToSave, hasChanges: hasChanges, totalPrice: totalPrice, onChange: onChange, onClear: onClear };
};
